"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

export const NAV_ITEMS = [
  { label: "能做什么", href: "#capabilities" },
  { label: "怎么用", href: "#how-it-works" },
  { label: "定价", href: "/pricing" },
  { label: "关于我们", href: "#personas" },
] as const;

export type LandingNavItem = {
  label: string;
  href: string;
};

type LandingNavProps = {
  href?: string;
  compact?: boolean;
  sticky?: boolean;
  withActivePricing?: boolean;
  ctaHref?: string;
  ctaLabel?: string;
  navItems?: readonly LandingNavItem[];
};

export function LandingNav({
  href = "/",
  compact = false,
  sticky = false,
  withActivePricing = false,
  ctaHref = "/editor/agent",
  ctaLabel = "免费开始",
  navItems = NAV_ITEMS,
}: LandingNavProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const isCondensed = compact || (sticky && isScrolled);

  useEffect(() => {
    if (!sticky) return;

    const updateScrolled = () => setIsScrolled(window.scrollY > 12);
    updateScrolled();
    window.addEventListener("scroll", updateScrolled, { passive: true });
    return () => window.removeEventListener("scroll", updateScrolled);
  }, [sticky]);

  return (
    <header
      style={sticky ? { position: "sticky", top: 0, zIndex: 1000, isolation: "isolate" } : undefined}
      className={`z-[80] transition-[padding,box-shadow,background-color] duration-300 ${
        sticky
          ? `${isCondensed ? "py-1" : "py-2"} sticky top-0 border-b border-[#eadfd5]/80 bg-white/82 px-1 shadow-[0_14px_34px_rgba(54,39,24,0.08)] backdrop-blur-2xl sm:px-2 lg:px-3`
          : "relative px-1 pt-1 sm:px-2 sm:pt-2 lg:px-3 lg:pt-3"
      }`}
    >
      <div
        className={`mx-auto flex max-w-[1536px] items-center border border-white/75 backdrop-blur-[22px] transition-[height,border-radius,padding,background-color] duration-300 ${
          sticky
            ? "bg-white/76 shadow-none"
            : "bg-white/48 shadow-[0_18px_60px_rgba(108,74,43,0.05)]"
        } ${
          isCondensed
            ? "h-[58px] gap-5 rounded-[16px] px-4 sm:px-6 lg:px-8"
            : compact
              ? "h-[78px] gap-8 rounded-[20px] px-5 sm:px-8 lg:px-10"
              : "h-[102px] gap-8 rounded-[32px] px-6 sm:px-8 lg:px-10"
        }`}
      >
        <Link
          href={href}
          className={`font-black leading-none text-[#071850] transition-[font-size] duration-300 ${
            isCondensed
              ? "text-[clamp(2rem,2.5vw,2.8rem)]"
              : compact
                ? "text-[clamp(2.6rem,3.6vw,4rem)]"
                : "text-[clamp(3rem,3.8vw,4.6rem)]"
          }`}
          aria-label="D8D 首页"
        >
          D8D
        </Link>

        <nav
          className={`hidden flex-1 items-center justify-start pl-2 transition-[gap] duration-300 md:flex ${
            isCondensed ? "gap-7 lg:gap-9" : "gap-10 lg:gap-12"
          }`}
        >
          {navItems.map((item) => {
            const isActive = withActivePricing && item.href === "/pricing";
            return (
              <Link
                key={item.label}
                href={item.href}
                className={`relative font-extrabold transition-[color,font-size] duration-300 hover:text-[#ff4b3e] ${
                  isCondensed ? "text-[0.94rem]" : "text-[1.03rem]"
                } ${
                  isActive ? "text-[#ff4b3e]" : "text-[#07163f]"
                }`}
              >
                {item.label}
                {isActive ? (
                  <span
                    className={`absolute left-1/2 h-1 -translate-x-1/2 rounded-full bg-[#ff4b3e] transition-[bottom,width] duration-300 ${
                      isCondensed ? "-bottom-[1rem] w-9" : "-bottom-[1.4rem] w-12"
                    }`}
                  />
                ) : null}
              </Link>
            );
          })}
        </nav>

        <Link
          href={ctaHref}
          className={`inline-flex shrink-0 items-center justify-center border border-white/85 bg-gradient-to-br from-[#ff9b1f] via-[#ff5c35] to-[#ff4a52] font-extrabold text-white shadow-[0_14px_30px_rgba(255,82,62,0.28)] transition-[height,border-radius,padding,font-size,transform] duration-300 hover:-translate-y-0.5 ${
            isCondensed
              ? "h-10 rounded-[12px] px-5 text-[0.92rem]"
              : "h-14 rounded-[18px] px-7 text-[1.02rem]"
          }`}
        >
          {ctaLabel}
        </Link>
      </div>
    </header>
  );
}
